import { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { auth } from '@/features/auth/firebase';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { PCM_CHUNK_PROCESSOR_NAME } from './pcmChunkProcessorName';
import pcmWorkletUrl from './pcmWorklet?worker&url';

export interface TranscriptEntry {
  text: string;
  speaker: string | null;
  timestamp: number;
}

interface LiveRecordingState {
  meetingId: string | null;
  isRecording: boolean;
  isPaused: boolean;
  transcripts: TranscriptEntry[];
  interimText: string;
  error: string | null;
  info: string | null;
  isSaving: boolean;
  startRecording: (meetingId: string) => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  setError: (error: string | null) => void;
  setIsSaving: (saving: boolean) => void;
  clearTranscripts: () => void;
}

const LiveRecordingContext = createContext<LiveRecordingState | null>(null);

export function useLiveRecording() {
  const ctx = useContext(LiveRecordingContext);
  if (!ctx) throw new Error('useLiveRecording must be used within LiveRecordingProvider');
  return ctx;
}

export function LiveRecordingProvider({ children }: { children: ReactNode }) {
  const [recordingMeetingId, setRecordingMeetingId] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([]);
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const isPausedRef = useRef(false);

  const stopRecording = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setIsPaused(false);
    isPausedRef.current = false;
    setInterimText('');
  }, []);

  const pauseRecording = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
  }, []);

  const resumeRecording = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
  }, []);

  const appendTranscript = useCallback((entry: TranscriptEntry) => {
    setTranscripts((prev) => [...prev, entry]);
  }, []);

  const startRecording = useCallback(
    async (meetingId: string) => {
      setError(null);
      setInfo(null);

      if (!('AudioWorkletNode' in window)) {
        setError("Live transcription isn't supported in this browser.");
        return;
      }

      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError('Please sign in to use live transcription');
          return;
        }
        const token = await currentUser.getIdToken();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }
        });
        streamRef.current = stream;

        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const sampleRate = audioContext.sampleRate;

        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;

        await audioContext.audioWorklet.addModule(pcmWorkletUrl);
        const workletNode = new AudioWorkletNode(audioContext, PCM_CHUNK_PROCESSOR_NAME, {
          numberOfOutputs: 0,
          channelCount: 1,
          channelCountMode: 'explicit'
        });
        workletNodeRef.current = workletNode;

        const apiUrl = import.meta.env.VITE_API_URL as string;
        const wsBase = apiUrl.replace(/^http/, 'ws');
        const tenantCode = resolveTenantCode(window.location.pathname);
        const wsPath = toTenantPath(`/meetings/${meetingId}/transcribe`, tenantCode);
        const wsUrl = `${wsBase}${wsPath}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          ws.send(JSON.stringify({ token, sampleRate, language: 'en-GB' }));
        };

        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'ready') {
            setRecordingMeetingId(meetingId);
            setIsRecording(true);

            workletNode.port.onmessage = (chunkEvent: MessageEvent<ArrayBuffer>) => {
              if (ws.readyState !== WebSocket.OPEN || isPausedRef.current) return;
              ws.send(chunkEvent.data);
            };

            source.connect(workletNode);
          } else if (data.type === 'transcript') {
            if (data.is_final) {
              appendTranscript({ text: data.transcript, speaker: data.speaker ?? null, timestamp: Date.now() });
              setInterimText('');
            } else {
              setInterimText(data.transcript);
            }
          } else if (data.type === 'error') {
            setError(data.message);
            stopRecording();
          } else if (data.type === 'info') {
            setInfo(data.message);
          }
        };

        ws.onerror = () => {
          setError('Connection error. Please try again.');
          stopRecording();
        };

        ws.onclose = () => {
          stopRecording();
        };
      } catch (err: any) {
        if (err.name === 'NotAllowedError') {
          setError('Microphone access denied. Please allow microphone access in your browser settings.');
        } else {
          setError(err.message || 'Failed to start recording');
        }
        stopRecording();
      }
    },
    [stopRecording, appendTranscript]
  );

  const clearTranscripts = useCallback(() => {
    setTranscripts([]);
    setInterimText('');
    setRecordingMeetingId(null);
    setError(null);
    setInfo(null);
    setIsSaving(false);
  }, []);

  useEffect(() => {
    if (!isRecording) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  const value = useMemo(
    () => ({
      meetingId: recordingMeetingId,
      isRecording,
      isPaused,
      transcripts,
      interimText,
      error,
      info,
      isSaving,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      setError,
      setIsSaving,
      clearTranscripts
    }),
    [
      recordingMeetingId,
      isRecording,
      isPaused,
      transcripts,
      interimText,
      error,
      info,
      isSaving,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      setError,
      setIsSaving,
      clearTranscripts
    ]
  );

  return <LiveRecordingContext.Provider value={value}>{children}</LiveRecordingContext.Provider>;
}
